import React, {
    createContext,
    useContext,
    useState,
    useEffect,
} from "react";
//import type { SongSuggestionType } from "@/types/types";

interface SuggestionSettingsContextType {
    suggestionLimit: number;
    setSuggestionLimit: (limit: number) => void;

    //suggestedSongs: SongSuggestionType[];
    //setSuggestedSongs: React.Dispatch<React.SetStateAction<SongSuggestionType[]>>;

    isMobile: boolean;

    isExpanded: boolean;
    setIsExpanded: (expanded: boolean) => void;

    removedIds: string[];
    addRemovedId: (id: string) => void;
    clearRemovedIds: () => void;
}

const SuggestionSettingsContext = createContext<
    SuggestionSettingsContextType | undefined
>(undefined);
const SUGGESTION_LIMIT_STORAGE_KEY = "suggestionLimit";

const getInitialSuggestionLimit = (): number => {
    if (typeof window === "undefined") return 5;
    const parsed = Number.parseInt(localStorage.getItem(SUGGESTION_LIMIT_STORAGE_KEY) || "5", 10);
    return Number.isFinite(parsed) ? Math.max(1, Math.min(20, parsed)) : 5;
};

export const SuggestionSettingsProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const [suggestionLimit, setSuggestionLimitState] = useState<number>(getInitialSuggestionLimit);
    //const [suggestedSongs, setSuggestedSongs] = useState<SongSuggestionType[]>([]);

    // ✅ device detection
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia?.("(max-width: 639px)");
        const setFlags = () => {
            const touch =
                "ontouchstart" in window || navigator.maxTouchPoints > 0;
            setIsMobile(!!mq?.matches || touch);
        };
        setFlags();
        mq?.addEventListener?.("change", setFlags);
        return () => mq?.removeEventListener?.("change", setFlags);
    }, []);

    // ✅ expansion (default true for desktop, auto-true for mobile)
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    useEffect(() => {
        if (isMobile) {
            setIsExpanded(true); // always open on mobile
        }
    }, [isMobile]);

    // ✅ removed songs
    const [removedIds, setRemovedIds] = useState<string[]>([]);
    const setSuggestionLimit = (limit: number) => {
        const normalized = Number.isFinite(limit) ? Math.max(1, Math.min(20, Math.floor(limit))) : 5;
        setSuggestionLimitState(normalized);
        if (typeof window !== "undefined") {
            localStorage.setItem(SUGGESTION_LIMIT_STORAGE_KEY, String(normalized));
        }
    };
    const addRemovedId = (id: string) =>
        setRemovedIds((prev) => [...prev, id]);
    const clearRemovedIds = () => setRemovedIds([]);

    return (
        <SuggestionSettingsContext.Provider
            value={{
                suggestionLimit,
                setSuggestionLimit,
                //suggestedSongs,
                //setSuggestedSongs,
                isMobile,
                isExpanded,
                setIsExpanded,
                removedIds,
                addRemovedId,
                clearRemovedIds,
            }}
        >
            {children}
        </SuggestionSettingsContext.Provider>
    );
};

export const useSuggestionSettings = () => {
    const context = useContext(SuggestionSettingsContext);
    if (!context) {
        throw new Error(
            "useSuggestionSettings must be used within SuggestionSettingsProvider"
        );
    }
    return context;
};
